import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { StatCard } from "@/components/stat-card"
import { ImprovedSignalCard } from "@/components/improved-signal-card"
import { EmptyState } from "@/components/empty-state"
import { ErrorState } from "@/components/error-state"
import { LoadingState } from "@/components/loading-state"
import { InternalNavbar } from "@/components/InternalNavbar"
import { PlanStatusBanner } from "@/components/PlanStatusBanner"
import { TrialExpiredBlock } from "@/components/TrialExpiredBlock"
import { useSignals } from "@/hooks/use-signals"
import { useLanguage } from "@/hooks/use-language"
import { useAuth } from "@/contexts/AuthContext"
import { useLicense } from "@/hooks/use-license"
import { useNavigate } from "react-router-dom"
import {
  Search,
  Filter,
  Zap,
  TrendingUp,
  Target,
  BarChart3,
  RefreshCw,
  WifiOff
} from "lucide-react"

export function Dashboard() {
  const { language } = useLanguage()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { licenseType, isPremium, isExpired, isTrial } = useLicense(user?.uid)
  const { signals, loading, error, isOfflineMode } = useSignals()
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [assetFilter, setAssetFilter] = useState("all")

  // Bloquear dashboard si el trial está expirado y no tiene premium
  const isBlocked = isTrial && isExpired && licenseType !== "premium" && licenseType !== "enterprise"

  // Si está bloqueado, mostrar solo el bloqueo
  if (isBlocked) {
    return (
      <div className="min-h-screen bg-background">
        <InternalNavbar />
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          <TrialExpiredBlock onUpgrade={() => navigate("/payment")} />
        </div>
      </div>
    )
  }

  // Filter signals based on search and filters
  const filteredSignals = signals.filter(signal => {
    const matchesSearch = signal.symbol.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === "all" || signal.status === statusFilter
    const matchesAsset = assetFilter === "all" || signal.symbol === assetFilter
    
    return matchesSearch && matchesStatus && matchesAsset
  })

  const displaySignals = filteredSignals

  // Calculate stats
  const activeSignals = signals.filter(s => s.status === "active").length
  const totalSignals = signals.length
  const resolvedSignals = signals.filter(
    (signal) => signal.status === "target-hit" || signal.status === "stop-hit"
  )
  const winningSignals = resolvedSignals.filter((signal) => signal.status === "target-hit").length
  const winRate = resolvedSignals.length > 0
    ? Math.round((winningSignals / resolvedSignals.length) * 100)
    : 0
  const realizedReturns = signals.filter((signal) => typeof signal.pnlPercent === "number")
  const averageReturn = realizedReturns.length > 0
    ? `${(
      realizedReturns.reduce((sum, signal) => sum + (signal.pnlPercent ?? 0), 0) /
      realizedReturns.length
    ).toFixed(1)}%`
    : "N/A"

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <InternalNavbar />
        <div className="container mx-auto px-4 py-8">
          <LoadingState message={language === "en" ? "Loading market signals..." : "Cargando señales del mercado..."} />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <InternalNavbar />
        <div className="container mx-auto px-4 py-8">
          <ErrorState
            title={language === "en" ? "Failed to Load Signals" : "Error al cargar señales"}
            description={language === "en"
              ? "Could not retrieve signals. Try again or check your connection."
              : "No se pudieron obtener las señales. Intenta nuevamente o revisa tu conexión."
            }
            retryLabel={language === "en" ? "Retry" : "Reintentar"}
            onRetry={() => window.location.reload()}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <InternalNavbar />
      
      <div className="container mx-auto px-4 py-8 space-y-8 max-w-7xl">
        {/* Plan Status Banner */}
        {user && <PlanStatusBanner />}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title={language === "en" ? "Active Signals" : "Señales Activas"}
            value={activeSignals.toString()}
            change={language === "en" ? "Currently stored in Firestore" : "Actualmente almacenadas en Firestore"}
            trend="neutral"
            icon={Zap}
            color="primary"
          />
          <StatCard
            title={language === "en" ? "Win Rate" : "Tasa de Acierto"}
            value={`${winRate}%`}
            change={resolvedSignals.length > 0
              ? language === "en" ? "Based on resolved stored signals" : "Basada en señales resueltas almacenadas"
              : language === "en" ? "No resolved signals yet" : "Aún no hay señales resueltas"}
            trend="neutral"
            icon={Target}
            color="primary"
            locked={!isPremium}
          />
          <StatCard
            title={language === "en" ? "Avg. Return" : "Retorno Promedio"}
            value={averageReturn}
            change={realizedReturns.length > 0
              ? language === "en" ? "Across stored closed trades" : "Sobre operaciones cerradas almacenadas"
              : language === "en" ? "No realized returns yet" : "Aún no hay retornos realizados"}
            trend="neutral"
            icon={TrendingUp}
            color="secondary"
            locked={!isPremium}
          />
          <StatCard
            title={language === "en" ? "Total Signals" : "Total de Señales"}
            value={totalSignals.toString()}
            change={language === "en" ? "Total stored in Firestore" : "Total almacenado en Firestore"}
            trend="neutral"
            icon={BarChart3}
            color="muted"
          />
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              {language === "en" ? "Filters" : "Filtros"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {language === "en" ? "Search signals..." : "Buscar señales..."}
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={language === "en" ? "Search signals..." : "Buscar señales..."}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {language === "en" ? "Status" : "Estado"}
                </label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {language === "en" ? "All Status" : "Todos los estados"}
                    </SelectItem>
                    <SelectItem value="active">
                      {language === "en" ? "Active" : "Activas"}
                    </SelectItem>
                    <SelectItem value="pending">
                      {language === "en" ? "Pending" : "Pendientes"}
                    </SelectItem>
                    <SelectItem value="observation">
                      {language === "en" ? "Observation" : "En observación"}
                    </SelectItem>
                    <SelectItem value="closed">
                      {language === "en" ? "Closed" : "Cerradas"}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {language === "en" ? "Asset" : "Activo"}
                </label>
                <Select value={assetFilter} onValueChange={setAssetFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {language === "en" ? "All Assets" : "Todos los activos"}
                    </SelectItem>
                    <SelectItem value="BTCUSDT">BTC/USDT</SelectItem>
                    <SelectItem value="ETHUSDT">ETH/USDT</SelectItem>
                    <SelectItem value="ADAUSDT">ADA/USDT</SelectItem>
                    <SelectItem value="SOLUSDT">SOL/USDT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {(searchTerm || statusFilter !== "all" || assetFilter !== "all") && (
              <div className="mt-4 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {filteredSignals.length} {language === "en" ? "signals found" : "señales encontradas"}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchTerm("")
                    setStatusFilter("all")
                    setAssetFilter("all")
                  }}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {language === "en" ? "Clear filters" : "Limpiar filtros"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Signals */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">
              {language === "en" ? "Recent Signals" : "Señales Recientes"}
            </h2>
            {isOfflineMode && (
              <Badge variant="outline" className="border-primary/30 text-primary bg-primary/10">
                <WifiOff className="w-3 h-3 mr-1" />
                {language === "en" ? "Offline Mode" : "Modo Offline"}
              </Badge>
            )}
          </div>
          
          {displaySignals.length === 0 && filteredSignals.length === 0 ? (
            <EmptyState
              title={isPremium
                ? language === "en" ? "No Stored Signals Yet" : "Aún no hay señales almacenadas"
                : language === "en" ? "Premium Access Required" : "Se requiere acceso Premium"}
              description={language === "en"
                ? isPremium
                  ? "Firestore does not have any stored signals yet. New persisted signals will appear here."
                  : "The live Firestore signal feed is currently visible only to active premium accounts."
                : isPremium
                  ? "Firestore aún no tiene señales almacenadas. Las nuevas señales persistidas aparecerán aquí."
                  : "El feed en vivo de Firestore solo está visible por ahora para cuentas premium activas."
              }
              actionLabel={!isPremium ? language === "en" ? "Upgrade to Premium" : "Adquirir Premium" : undefined}
              onAction={!isPremium ? () => navigate("/payment") : undefined}
            />
          ) : (
            <div className="space-y-4">
              {displaySignals.map((signal) => (
                <ImprovedSignalCard
                  key={signal.id}
                  signal={signal}
                  language={language}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

